import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  Circle,
  Pencil,
  Star,
  Trash2,
} from "lucide-react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { motion, AnimatePresence } from "framer-motion";

import Button from "../../../../../shared/ui/button/Button";
import TimelinePhaseViewModal from "./TimelinePhaseViewModal";
import type { Session } from "../../../../session";
import { listPlanSubjectSessions } from "../../../application/planSessions";

import DOMPurify from "dompurify";
import { TherapeuticPhase } from "../../../domain/TherapeuticPlan";
import { formatDate } from "../../../../../shared/utils/date";
import { blockNoteToHtml } from "../../../../../shared/utils/blockNote";

interface Props {
  patientId?: string;
  isCouple?: boolean;
  timeline: TherapeuticPhase[];
  onChange: (timeline: TherapeuticPhase[]) => void;
}

const EMPTY_PHASE = (): TherapeuticPhase => ({
  id: crypto.randomUUID(),
  title: "",
  description: "",
  internal_notes: "",
  linked_sessions: [],
  completed: false,
  order: 999,
});

export default function TherapeuticPlanViewTimeline({
  patientId,
  isCouple,
  timeline,
  onChange,
}: Props) {
  const { t, i18n } = useTranslation();
  const [editingPhase, setEditingPhase] = useState<TherapeuticPhase | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [sessions, setSessions] = useState<{ value: string; text: string; date: string; hour: string; status?: string }[]>([]);
  const [expandedPhases, setExpandedPhases] = useState<string[]>(timeline.map(p => p.id));

  const togglePhase = (id: string, e: React.MouseEvent) => {
    // Don't toggle if clicking buttons
    if ((e.target as HTMLElement).closest('button')) return;

    setExpandedPhases(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  // 🔹 Load sessions
  useEffect(() => {
    if (!patientId) {
      setSessions([]);
      return;
    }

    listPlanSubjectSessions(patientId, isCouple, { offset: 0, limit: 100 })
      .then((sessions) => {
        const data = sessions ?? [];

        // ...
        setSessions(
          data.map((s: Session) => ({
            value: s.id,
            text: s.title,
            date: formatDate(s.date, i18n.language),
            hour: s.hour ? ` ${s.hour}` : "",
            status: s.status,
          }))
        );
      })
      .catch(console.error);
  }, [patientId, isCouple, i18n.language]);

  // 🔹 Create new phase
  const openCreatePhase = () => {
    setEditingPhase(EMPTY_PHASE());
    setIsCreating(true);
  };

  // 🔹 Save (create or edit)
  const savePhase = (phase: TherapeuticPhase) => {
    if (isCreating) {
      const maxOrder = timeline.reduce((max, p) => Math.max(max, p.order), 0);
      phase.order = maxOrder + 1;
      onChange([...timeline, phase]);
    } else {
      onChange(timeline.map((p) => (p.id === phase.id ? phase : p)));
    }

    setEditingPhase(null);
    setIsCreating(false);
  };

  // 🔹 Delete
  const deletePhase = (id: string) => {
    onChange(timeline.filter((p) => p.id !== id));
  };

  // 🔹 Drag & Drop
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const newTimeline = Array.from(timeline).sort((a, b) => a.order - b.order);
    const [moved] = newTimeline.splice(result.source.index, 1);
    newTimeline.splice(result.destination.index, 0, moved);

    // Re-assign orders
    const updated = newTimeline.map((p, i) => ({ ...p, order: i }));
    onChange(updated);
  };

  const sortedTimeline = [...timeline].sort((a, b) => a.order - b.order);

  return (
    <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-sm space-y-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5" />
          </div>
          <h3 className="text-xl font-bold text-content-primary">
            {t("therapeuticPlan.timeline")}
          </h3>
        </div>
        <Button
          onClick={openCreatePhase}
          className="rounded-xl px-6 shadow-lg shadow-brand-500/20 w-full sm:w-auto"
        >
          {t("therapeuticPlan.addPhase")}
        </Button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="timeline">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="relative pt-4 space-y-12"
            >
              {/* Vertical Timeline Line */}
              <div className="absolute left-[12px] lg:left-1/2 top-2 bottom-8 w-1 -translate-x-1/2 bg-gradient-to-b from-brand-500/50 via-brand-500/30 to-brand-500/10 rounded-full dark:from-brand-500/30 dark:to-transparent" />

              {sortedTimeline.map((phase, index) => {
                const isExpanded = expandedPhases.includes(phase.id);
                const activeIndex = sortedTimeline.findIndex(p => !p.completed);
                const isActive = index === activeIndex;
                const isLast = index === sortedTimeline.length - 1;

                return (
                  <Draggable key={phase.id} draggableId={phase.id} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`relative group w-full flex flex-col ${index % 2 === 0 ? 'lg:items-end' : 'lg:items-start'} pl-10 lg:pl-0`}
                      >
                        {/* Status Marker on the timeline */}
                        <div className={`absolute left-[12px] lg:left-1/2 top-6 -translate-x-1/2 z-10 transition-all duration-500 ${isActive ? 'scale-125' : ''}`}>
                          <div className={`w-5 h-5 lg:w-11 lg:h-11 rounded-full border-2 lg:border-4 bg-white dark:bg-gray-950 flex items-center justify-center shadow-lg transition-all duration-300 ${phase.completed ? 'border-zen-indigo bg-zen-indigo text-white' :
                            isActive ? 'border-brand-500 bg-brand-500 text-white shadow-brand-500/40' :
                              'border-gray-200 text-content-tertiary group-hover:border-brand-300'
                            }`}>
                            {phase.completed ? (
                              <Check className="w-4 h-4 lg:w-5 h-5" />
                            ) : isActive ? (
                              <Activity className="w-3 h-3 lg:w-4 h-4" />
                            ) : isLast ? (
                              <Star className="w-3 h-3 lg:w-4 h-4" />
                            ) : (
                              <Circle className="w-3 h-3 lg:w-4 h-4" />
                            )}
                          </div>
                        </div>

                        {/* Phase Card */}
                        <div
                          onClick={(e) => togglePhase(phase.id, e)}
                          className={`
                            group relative p-6 rounded-3xl border transition-all duration-300 cursor-pointer overflow-hidden w-full lg:w-[45%]
                            ${isActive
                              ? 'bg-gradient-to-br from-brand-50/50 to-white/80 dark:from-brand-500/10 dark:to-white/[0.02] border-brand-500/50 shadow-theme-md ring-1 ring-brand-500/10'
                              : 'bg-white/60 dark:bg-white/[0.01] border-gray-100 dark:border-white/5 hover:border-brand-400/50 hover:shadow-theme-md'
                            }
                            ${isExpanded ? 'shadow-theme-lg' : 'shadow-theme-sm'}
                          `}
                        >
                          {/* Left Accent Stripe */}
                          <div className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 ${phase.completed ? 'bg-zen-indigo' :
                            isActive ? 'bg-brand-500' :
                              'bg-gray-200 dark:bg-white/10 group-hover:bg-brand-400'
                            }`} />

                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center flex-wrap gap-2 mb-2">
                                <h3 className={`font-bold transition-colors ${isActive ? 'text-brand-700 dark:text-white text-lg' : 'text-gray-800 dark:text-gray-200 text-base'}`}>
                                  {phase.title || t("therapeuticPlan.untitledPhase")}
                                </h3>

                                <div className="flex gap-2">
                                  {phase.completed ? (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-purple-50 text-zen-indigo dark:bg-purple-900/20 dark:text-zen-indigo border border-purple-100 dark:border-zen-indigo/20">
                                      {t("common.completed")}
                                    </span>
                                  ) : isActive ? (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400 border border-brand-100 dark:border-brand-500/20 animate-pulse">
                                      {t("common.active")}
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-gray-50 text-gray-500 dark:bg-white/5 dark:text-gray-400 border border-gray-100 dark:border-white/10">
                                      {t("common.upcoming")}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                    className="overflow-hidden"
                                  >
                                    <div className="mt-4 prose prose-sm dark:prose-invert">
                                      <div
                                        className="text-sm text-content-secondary leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blockNoteToHtml(phase.description) || t("therapeuticPlan.noDescription")) }}
                                      />
                                    </div>

                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>

                            <div className="flex gap-2 flex-shrink-0">
                              <div className="hidden lg:flex gap-2 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300">
                                <button
                                  className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 transition-all"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingPhase(phase);
                                    setIsCreating(false);
                                  }}
                                  title={t("common.edit")}
                                >
                                  <Pencil className="w-5 h-5" />
                                </button>
                                <button
                                  className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deletePhase(phase.id);
                                  }}
                                  title={t("common.delete")}
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                              <div className="p-2 rounded-xl text-content-tertiary group-hover:text-gray-600 dark:group-hover:text-gray-200 transition-colors">
                                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                              </div>
                            </div>
                          </div>

                          {/* Linked Sessions Chips */}
                          {phase.linked_sessions.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-5">
                              {phase.linked_sessions.map((id: string) => {
                                const s = sessions.find((s) => s.value === id);
                                if (!s) return null;
                                return (
                                  <div
                                    key={id}
                                    className="flex items-center px-3 py-1.5 bg-white dark:bg-white/[0.05] text-gray-600 dark:text-gray-300 rounded-xl text-xs font-bold gap-2 border border-gray-100 dark:border-white/5 shadow-theme-xs group/chip hover:border-brand-400 transition-all"
                                  >
                                    <Calendar className="hidden sm:inline-block w-3.5 h-3.5 text-brand-500 opacity-70 group-hover/chip:scale-110 transition-transform" />
                                    <span className="hidden sm:inline-block">{s.date}</span>
                                    <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                                    <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                                    <span className="truncate max-w-[150px] font-bold text-gray-900 dark:text-gray-100">{s.text}</span>
                                    {s.status && s.status !== 'completed' && (
                                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] uppercase border ${s.status === 'pending'
                                          ? 'bg-gray-50 text-gray-500 border-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
                                          : s.status === 'processing'
                                            ? 'bg-accent-tint text-accent-text border-accent/20'
                                            : s.status === 'review'
                                              ? 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20'
                                              : 'bg-red-50 text-red-600 border-red-100 dark:bg-red-900/40 dark:text-red-400 dark:border-red-800/20'
                                        }`}>
                                        {t(`session.statusOptions.${s.status}`, s.status)}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Mobile Actions Container */}
                          <div className="flex lg:hidden justify-end gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-white/5">
                            <button
                              className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-gray-50 text-gray-600 hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800/50 dark:text-gray-300 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 transition-all text-xs font-bold gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPhase(phase);
                                setIsCreating(false);
                              }}
                            >
                              <Pencil className="w-4 h-4" />
                              {t("common.edit")}
                            </button>
                            <button
                              className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-red-50/50 text-red-600 hover:bg-red-50 hover:text-red-700 dark:bg-red-500/5 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300 transition-all text-xs font-bold gap-2 border border-red-100/50 dark:border-red-500/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                deletePhase(phase.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                              {t("common.delete")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {editingPhase && (
        <TimelinePhaseViewModal
          phase={editingPhase}
          sessions={sessions}
          isCreating={isCreating}
          onClose={() => {
            setEditingPhase(null);
            setIsCreating(false);
          }}
          onSave={savePhase}
        />
      )}
    </div>
  );
}


